/**
 * TOKEN CONFIGURATION SYSTEM
 *
 * Dynamically loads token addresses from environment variables
 * Supports multiple blockchain networks and environments
 */

export interface TokenConfig {
  symbol: string;
  name: string;
  decimals: number;
  isStablecoin: boolean;
  networks: Record<string, string>; // network -> contract address
}

export interface NetworkConfig {
  name: string;
  chainId: number;
  rpcUrl: string;
  blockExplorer: string;
}

/**
 * Supported blockchain networks
 */
export const SUPPORTED_NETWORKS: Record<string, NetworkConfig> = {
  BSC_MAINNET: {
    name: 'Binance Smart Chain',
    chainId: 56,
    rpcUrl: process.env.REACT_APP_BSC_RPC_URL || 'https://bsc-dataseed.binance.org/',
    blockExplorer: 'https://bscscan.com',
  },
  BSC_TESTNET: {
    name: 'Binance Smart Chain Testnet',
    chainId: 97,
    rpcUrl:
      process.env.REACT_APP_BSC_TESTNET_RPC_URL ||
      'https://data-seed-prebsc-1-s1.binance.org:8545/',
    blockExplorer: 'https://testnet.bscscan.com',
  },
  ETHEREUM_MAINNET: {
    name: 'Ethereum Mainnet',
    chainId: 1,
    rpcUrl: process.env.REACT_APP_ETH_RPC_URL || 'https://mainnet.infura.io/v3/',
    blockExplorer: 'https://etherscan.io',
  },
  ETHEREUM_SEPOLIA: {
    name: 'Ethereum Sepolia',
    chainId: 11155111,
    rpcUrl: process.env.REACT_APP_ETH_SEPOLIA_RPC_URL || 'https://sepolia.infura.io/v3/',
    blockExplorer: 'https://sepolia.etherscan.io',
  },
};

/**
 * Get the current network from environment
 */
export function getCurrentNetwork(): string {
  return process.env.REACT_APP_NETWORK || 'BSC_MAINNET';
}

/**
 * Get current network configuration
 */
export function getCurrentNetworkConfig(): NetworkConfig {
  const network = getCurrentNetwork();
  return SUPPORTED_NETWORKS[network] || SUPPORTED_NETWORKS.BSC_MAINNET;
}

/**
 * Load token configurations from environment variables
 * Environment variables should follow the pattern:
 * REACT_APP_TOKEN_{SYMBOL}_{NETWORK} = "0x..."
 *
 * Examples:
 * REACT_APP_TOKEN_USDT_BSC_MAINNET="0x55d398326f99059fF775485246999027B3197955"
 * REACT_APP_TOKEN_USDT_BSC_TESTNET="0x337610d27c682E347C9cD60BD4b3b107C9d34dDd"
 * REACT_APP_TOKEN_ETH_BSC_MAINNET="0x2170Ed0880ac9A755fd29B2688956BD959F933F8"
 */
function loadTokenConfigs(): TokenConfig[] {
  const tokens: TokenConfig[] = [];

  // Define supported tokens and their properties
  const tokenDefinitions = [
    { symbol: 'USDT', name: 'Tether USD', decimals: 6, isStablecoin: true },
    { symbol: 'USDC', name: 'USD Coin', decimals: 6, isStablecoin: true },
    { symbol: 'DAI', name: 'Dai Stablecoin', decimals: 18, isStablecoin: true },
    { symbol: 'ETH', name: 'Ethereum', decimals: 18, isStablecoin: false },
    { symbol: 'BNB', name: 'Binance Coin', decimals: 18, isStablecoin: false },
  ];

  for (const tokenDef of tokenDefinitions) {
    const networks: Record<string, string> = {};

    // Load addresses for each supported network
    for (const [networkKey] of Object.entries(SUPPORTED_NETWORKS)) {
      const envVar = `REACT_APP_TOKEN_${tokenDef.symbol}_${networkKey}`;
      const address = process.env[envVar];

      if (address) {
        networks[networkKey] = address;
      }
    }

    // Only include tokens that have at least one network address
    if (Object.keys(networks).length > 0) {
      tokens.push({
        ...tokenDef,
        networks,
      });
    }
  }

  return tokens;
}

/**
 * Get all supported tokens for the current network
 */
export function getSupportedTokens(): TokenConfig[] {
  const currentNetwork = getCurrentNetwork();
  const allTokens = loadTokenConfigs();

  // Return tokens that have an address for the current network
  return allTokens.filter((token) => token.networks[currentNetwork]);
}

/**
 * Get token configuration by symbol for current network
 */
export function getTokenConfig(symbol: string): TokenConfig | null {
  const currentNetwork = getCurrentNetwork();
  const tokens = loadTokenConfigs();

  const token = tokens.find((t) => t.symbol === symbol);
  if (!token || !token.networks[currentNetwork]) {
    return null;
  }

  return token;
}

/**
 * Get contract address for a token symbol on current network
 */
export function getTokenAddress(symbol: string): string | null {
  const token = getTokenConfig(symbol);
  if (!token) return null;

  const currentNetwork = getCurrentNetwork();
  return token.networks[currentNetwork] || null;
}

/**
 * Get all supported token symbols for current network
 */
export function getSupportedTokenSymbols(): string[] {
  return getSupportedTokens().map((token) => token.symbol);
}

/**
 * Validate if a token is supported on current network
 */
export function isTokenSupported(symbol: string): boolean {
  return getTokenAddress(symbol) !== null;
}

/**
 * Get token decimals
 */
export function getTokenDecimals(symbol: string): number {
  const token = getTokenConfig(symbol);
  return token?.decimals || 18; // Default to 18 decimals
}

/**
 * Check if token is a stablecoin
 */
export function isStablecoin(symbol: string): boolean {
  const token = getTokenConfig(symbol);
  return token?.isStablecoin || false;
}

/**
 * Get token display name
 */
export function getTokenName(symbol: string): string {
  const token = getTokenConfig(symbol);
  return token?.name || symbol;
}

/**
 * Export configuration for debugging
 */
export function getTokenConfigSummary() {
  const currentNetwork = getCurrentNetwork();
  const tokens = getSupportedTokens();

  return {
    network: currentNetwork,
    networkConfig: getCurrentNetworkConfig(),
    supportedTokens: tokens.map((token) => ({
      symbol: token.symbol,
      name: token.name,
      address: token.networks[currentNetwork],
      decimals: token.decimals,
      isStablecoin: token.isStablecoin,
    })),
  };
}
